import React, { useState, useMemo } from 'react';
import { Monitor, Smartphone, Search, CheckCircle2, AlertTriangle, Info } from 'lucide-react';

const DIAG_LABELS = [
  { key: 'title_missing', label: 'Title Missing', bad: true },
  { key: 'title_too_long', label: 'Title > 60 chars', bad: true },
  { key: 'title_too_short', label: 'Title < 50 chars', warn: true },
  { key: 'title_optimal', label: 'Title 50-60 chars', good: true },
  { key: 'description_missing', label: 'Description Missing', bad: true },
  { key: 'description_too_long', label: 'Description > 160 chars', bad: true },
  { key: 'description_too_short', label: 'Description < 140 chars', warn: true },
  { key: 'description_optimal', label: 'Description 140-160 chars', good: true },
];

const GooglePreview = ({ preview, device }) => {
  const data = preview[device];
  return (
    <div className="bg-white rounded-xl p-4 flex flex-col gap-1.5 font-sans max-w-md mx-auto shadow-lg w-full">
      <div className="flex items-center gap-2">
        <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-[10px] text-gray-500 font-bold">
          {preview.raw_title?.charAt(0)?.toUpperCase() || 'G'}
        </div>
        <div className="flex flex-col">
          <span className="text-[10px] text-gray-500 font-sans">{preview.display_url}</span>
          <span className="text-[9px] text-gray-400">Sponsored · Ad preview shown for layout demo</span>
        </div>
      </div>
      <span className="text-[13px] leading-snug text-[#1a0dab] font-medium">
        {data.title || <span className="italic text-gray-400">(No title)</span>}
      </span>
      <span className="text-[11px] text-[#006621] font-sans">{data.display_url}</span>
      <span className="text-[11px] text-gray-700 leading-snug">
        {data.description || <span className="italic text-gray-400">(No meta description)</span>}
      </span>
    </div>
  );
};

const SerpPreviewPanel = ({ project, selectedPageFilter = 'ALL' }) => {
  const pages = useMemo(() => {
    const summaries = project?.technical_audit?.page_summaries || [];
    const withSerp = summaries.filter((p) => p.serp_preview);
    if (!selectedPageFilter || selectedPageFilter === 'ALL') return withSerp;
    return withSerp.filter((p) => p.url === selectedPageFilter);
  }, [project, selectedPageFilter]);

  const [selectedUrl, setSelectedUrl] = useState(pages[0]?.url || '');

  if (!pages.length) {
    return (
      <div className="bg-black/30 border border-gray-850 p-8 rounded-2xl text-center animate-in fade-in duration-300">
        <Search size={32} className="text-gray-700 mx-auto mb-3" />
        <p className="text-sm text-gray-500">
          No SERP previews available. Re-run an audit to see how your titles and meta descriptions render on Google.
        </p>
      </div>
    );
  }

  const current = pages.find((p) => p.url === selectedUrl) || pages[0];
  const preview = current.serp_preview;
  const diag = preview.diagnostics || {};
  const activeDiag = DIAG_LABELS.filter((d) => diag[d.key]);

  return (
    <div className="flex flex-col gap-6 animate-in fade-in duration-300">

      {/* Page selector */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-3.5 flex flex-col sm:flex-row justify-between sm:items-center gap-3">
        <div className="flex items-center gap-2">
          <Monitor size={16} className="text-blue-400 shrink-0" />
          <span className="text-xs font-bold text-white uppercase tracking-wider">SERP Preview For Page:</span>
        </div>
        <select
          value={current.url}
          onChange={(e) => setSelectedUrl(e.target.value)}
          className="bg-black/60 border border-gray-700 text-blue-300 font-mono text-xs rounded-lg px-3 py-1.5 focus:outline-none focus:border-blue-500 max-w-xs sm:max-w-md truncate cursor-pointer font-semibold shadow-inner"
        >
          {pages.map((p, idx) => (
            <option key={idx} value={p.url}>
              📄 {p.url}
            </option>
          ))}
        </select>
      </div>

      {/* Preview grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-black/35 border border-gray-850 rounded-2xl p-5 flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <Monitor size={15} className="text-blue-400" />
            <h4 className="font-extrabold text-white text-sm">Desktop Result</h4>
            {preview.desktop.title_truncated && (
              <span className="text-[9px] font-mono text-amber-300 bg-amber-950/50 border border-amber-500/25 px-1.5 py-0.5 rounded ml-auto">
                Title truncated
              </span>
            )}
          </div>
          <GooglePreview preview={preview} device="desktop" />
        </div>

        <div className="bg-black/35 border border-gray-850 rounded-2xl p-5 flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <Smartphone size={15} className="text-green-400" />
            <h4 className="font-extrabold text-white text-sm">Mobile Result</h4>
            {preview.mobile.title_truncated && (
              <span className="text-[9px] font-mono text-amber-300 bg-amber-950/50 border border-amber-500/25 px-1.5 py-0.5 rounded ml-auto">
                Title truncated
              </span>
            )}
          </div>
          <GooglePreview preview={preview} device="mobile" />
        </div>
      </div>

      {/* Diagnostics */}
      <div className="bg-black/30 border border-gray-850 rounded-2xl p-5 flex flex-col gap-3">
        <h4 className="font-extrabold text-white text-sm flex items-center gap-2">
          <Info size={15} className="text-purple-400" />
          Length Diagnostics
        </h4>
        <div className="flex flex-wrap gap-2">
          {activeDiag.length === 0 && (
            <span className="text-xs text-gray-500 italic">No diagnostics to show.</span>
          )}
          {activeDiag.map((d, i) => (
            <span
              key={i}
              className={`px-2.5 py-1 rounded-lg text-[10px] font-bold font-mono flex items-center gap-1.5 ${
                d.bad ? 'bg-red-950/40 text-red-300 border border-red-500/30'
                : d.good ? 'bg-green-950/40 text-green-300 border border-green-500/30'
                : 'bg-amber-950/40 text-amber-300 border border-amber-500/30'
              }`}
            >
              {d.bad ? <AlertTriangle size={10} /> : d.good ? <CheckCircle2 size={10} /> : <AlertTriangle size={10} />}
              {d.label} ({d.key.includes('title') ? preview.title_length : preview.description_length} chars)
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SerpPreviewPanel;
